import React, { useState, useEffect } from 'react';
import { Megaphone, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Announcement {
  id: string;
  title: string;
  body: string;
  type: 'info' | 'success' | 'warning' | 'update';
  pinned: boolean;
  created_at: string;
}

const typeStyles: Record<string, { border: string; badge: string; label: string }> = {
  info:    { border: 'border-l-electric',  badge: 'bg-electric/10 text-electric',   label: 'Info' },
  success: { border: 'border-l-emerald',   badge: 'bg-emerald/10 text-emerald',     label: 'News' },
  warning: { border: 'border-l-gold',      badge: 'bg-gold/10 text-gold',           label: 'Notice' },
  update:  { border: 'border-l-fiery',     badge: 'bg-fiery/10 text-fiery',         label: 'Update' },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('default', { month: 'short', day: 'numeric' });
}

export function AnnouncementsSection() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    supabase
      .from('announcements')
      .select('id, title, body, type, pinned, created_at')
      .order('pinned', { ascending: false })
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setAnnouncements(data ?? []);
        setLoading(false);
      });
  }, []);

  if (!loading && announcements.length === 0) {
    return null;
  }

  const visible = showAll ? announcements : announcements.slice(0, 3);

  return (
    <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm border border-sand/20 dark:border-slate/20">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-sand/20 dark:border-slate/20">
        <Megaphone className="h-4 w-4 text-slate dark:text-soft-white/60" />
        <h2 className="text-sm font-semibold text-midnight dark:text-soft-white">
          Updates from SermonBuild
        </h2>
      </div>

      {loading ? (
        <div className="divide-y divide-sand/20 dark:divide-slate/20">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="px-4 py-3 animate-pulse space-y-1.5">
              <div className="h-3.5 bg-sand/40 rounded w-40" />
              <div className="h-3 bg-sand/40 rounded w-full" />
              <div className="h-3 bg-sand/40 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="divide-y divide-sand/20 dark:divide-slate/20">
            {visible.map((a) => {
              const style = typeStyles[a.type] ?? typeStyles.info;
              return (
                <div
                  key={a.id}
                  className={`pl-3 pr-4 py-3 border-l-4 ${style.border}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {a.pinned && (
                          <Star className="h-3 w-3 text-gold fill-gold shrink-0" />
                        )}
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${style.badge}`}>
                          {style.label}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-midnight dark:text-soft-white leading-snug">
                        {a.title}
                      </p>
                      <p className="text-xs text-slate dark:text-soft-white/60 mt-0.5 leading-relaxed">
                        {a.body}
                      </p>
                    </div>
                    <span className="text-[10px] text-slate/60 dark:text-soft-white/40 shrink-0 mt-0.5">
                      {formatDate(a.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {announcements.length > 3 && (
            <div className="px-4 py-2.5 border-t border-sand/20 dark:border-slate/20">
              <button
                onClick={() => setShowAll(!showAll)}
                className="flex items-center gap-1 text-xs text-electric hover:text-electric/80 transition-colors"
              >
                {showAll ? (
                  <><ChevronUp className="h-3.5 w-3.5" /> Show less</>
                ) : (
                  <><ChevronDown className="h-3.5 w-3.5" /> Show {announcements.length - 3} more</>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
